import { useState, useEffect, useRef } from 'react'
import { Phone, Mail, ShieldAlert } from 'lucide-react'
import api from '@/lib/api/axios'

interface SupportInfo {
    id: number
    description: string
    value: string
}

interface SupportDropdownProps {
    open: boolean
    onClose: () => void
}

export function SupportDropdown({ open, onClose }: SupportDropdownProps) {
    const ref = useRef<HTMLDivElement>(null)
    const [supportInfo, setSupportInfo] = useState<SupportInfo[]>([])

    useEffect(() => {
        if (open) {
            api.get('/api/diagnostics/support-info')
                .then(res => {
                    const data = (res.data as any)?.data || res.data
                    if (Array.isArray(data)) setSupportInfo(data)
                })
                .catch(() => {})
        }
    }, [open])

    // Close on click outside
    useEffect(() => {
        if (!open) return
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                onClose()
            }
        }
        setTimeout(() => document.addEventListener('mousedown', handler), 0)
        return () => document.removeEventListener('mousedown', handler)
    }, [open, onClose])

    if (!open) return null

    const phoneInfo = supportInfo.find(s => s.description.toLowerCase().includes('phone') && !s.description.toLowerCase().includes('goteam'))
    const emailInfo = supportInfo.find(s => s.description.toLowerCase().includes('email'))
    const goTeamPhone = supportInfo.find(s => s.description === 'GoTeam')
    const goTeamText = supportInfo.find(s => s.description === 'GoTeamText')

    return (
        <div
            ref={ref}
            className="absolute top-[calc(var(--topbar-height)-4px)] left-4 z-[250] w-80 rounded-lg border bg-popover shadow-lg animate-in fade-in-0 slide-in-from-top-2 duration-150"
        >
            <div className="p-4 space-y-3">
                {/* Support contacts */}
                {phoneInfo && (
                    <div className="flex items-center gap-3 text-sm">
                        <div className="h-8 w-8 rounded-full bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center flex-shrink-0">
                            <Phone className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                            <p className="text-xs text-muted-foreground">{phoneInfo.description}</p>
                            <a href={`tel:${phoneInfo.value.replace(/\s/g, '')}`} className="font-medium hover:text-primary">
                                {phoneInfo.value}
                            </a>
                        </div>
                    </div>
                )}
                {emailInfo && (
                    <div className="flex items-center gap-3 text-sm">
                        <div className="h-8 w-8 rounded-full bg-green-50 dark:bg-green-950/30 flex items-center justify-center flex-shrink-0">
                            <Mail className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                            <p className="text-xs text-muted-foreground">{emailInfo.description}</p>
                            <a href={`mailto:${emailInfo.value}`} className="font-medium hover:text-primary">
                                {emailInfo.value}
                            </a>
                        </div>
                    </div>
                )}

                {/* GoTeam */}
                {goTeamPhone && (
                    <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20 p-2.5 space-y-1.5">
                        <div className="flex items-center gap-2">
                            <ShieldAlert className="h-4 w-4 text-red-600 flex-shrink-0" />
                            <span className="text-xs font-semibold text-red-700 dark:text-red-400">GoTeam</span>
                            <a href={`tel:${goTeamPhone.value.replace(/\s/g, '')}`} className="text-xs font-medium text-red-700 dark:text-red-400 hover:underline ml-auto">
                                {goTeamPhone.value}
                            </a>
                        </div>
                        {goTeamText && (
                            <p className="text-[11px] text-red-600 dark:text-red-400 leading-tight">
                                {goTeamText.value}
                            </p>
                        )}
                    </div>
                )}

            </div>
        </div>
    )
}
